import PageLayout from '../../layout/PageLayout/PageLayout';
import VerifyStaffAccountContext, {
    EVerifyStaffAccountUiState,
    VerifyStaffAccountProvider,
} from '../../contexts/VerifyStaffAccountContext';
import CardLoader from '../../components/Loader/Loader';
import styles from './VerifyStaffAccountScreen.module.scss';
import { Redirect } from 'react-router-dom';
import { autoLoginSeconds, ERoutes } from '../../util/constants';
import Card from '../../components/Card/Card';
import common from '../../util/scss/common.module.scss';
import { IconSuccess } from '../../icons/Icons';
import GlobalContext from '../../contexts/GlobalContext';
import { replacePhrase } from '../../util/helpers';
import API from '../../api/api';
import { Button } from '@mui/material';
import CoBranding from '../../components/CoBranding/CoBranding';
import { FC, useContext, useEffect, useMemo, useState } from 'react';

const VerifyStaffAccountSuccess: FC = () => {
    const { language, fullLoginLink, removeFullLoginLinkFromLocalStorage, coBranding } = useContext(GlobalContext);
    const [secondsLeft, setSecondsLeft] = useState(autoLoginSeconds);

    const autoLoginDesc = useMemo(() => {
        const desc1 = replacePhrase(language.verifyStaff.autoLogin, '<secondsLeft>', secondsLeft.toString());
        const desc2 = replacePhrase(
            desc1,
            '<secondsLabel>',
            secondsLeft > 1 ? language.common.seconds : language.common.second,
        );
        return desc2;
    }, [secondsLeft]);

    useEffect(() => {
        const loginTimer = setTimeout(() => {
            setSecondsLeft(secondsLeft - 1);
        }, 1000);
        return () => clearTimeout(loginTimer);
    }, [secondsLeft]);

    useEffect(() => {
        secondsLeft === 0 && fullLoginLink && handleLogin();
    }, [secondsLeft]);

    useEffect(() => {
        API.post.trackEvent({ event: 'Sign In || Staff Verification Complete' });
    }, []);

    const handleLogin = () => {
        removeFullLoginLinkFromLocalStorage();
        window.location.assign(fullLoginLink);
    };

    return (
        <Card>
            {(coBranding.logoUrl || coBranding.name) && (
                <CoBranding logoUrl={coBranding.logoUrl} name={coBranding.name} />
            )}
            <section className={`${common.icon}`}>
                <IconSuccess width={'50'} height={'50'} fillColor={'777982'} />
            </section>
            <header>
                <h1 className={`${common.pageHeader} ${common.withSubhead}`}>{language.verifyStaff.header}</h1>
                <p className={`${common.pageSubhead}`} data-testid={'verify-desc'}>
                    {language.verifyStaff.desc.generic}
                </p>
            </header>
            {fullLoginLink && (
                <footer className={styles.login}>
                    <Button variant="contained" type="button" onClick={handleLogin} size="large">
                        {language.verifyStaff.loginCta}
                    </Button>
                    <p>{autoLoginDesc}</p>
                </footer>
            )}
        </Card>
    );
};

const VerifyStaffAccountUi: FC = () => {
    const { verifyStaffAccountUiState } = useContext(VerifyStaffAccountContext);
    let verifyStaffAccountUi: React.ReactElement;
    switch (verifyStaffAccountUiState) {
        case EVerifyStaffAccountUiState.default:
        case EVerifyStaffAccountUiState.initialzed:
            verifyStaffAccountUi = <CardLoader />;
            break;
        case EVerifyStaffAccountUiState.ready:
            verifyStaffAccountUi = <VerifyStaffAccountSuccess />;
            break;
        case EVerifyStaffAccountUiState.fatalError:
            verifyStaffAccountUi = <Redirect to={ERoutes.error} />;
            break;
        default:
            throw new Error('Verify Account UI State set incorrectly');
    }
    return verifyStaffAccountUi;
};

const VerifyStaffAccountScreen: FC = () => {
    return (
        <VerifyStaffAccountProvider>
            <PageLayout>
                <VerifyStaffAccountUi />
            </PageLayout>
        </VerifyStaffAccountProvider>
    );
};

export default VerifyStaffAccountScreen;
